var AbsoluteShowingQueueSupport = require("enyo-perf/AbsoluteShowingQueueSupport"),
    AccelerateSupport           = require("enyo-ui/AccelerateSupport"),
    Animation                   = require("enyo-anim/Animation"),
    AnimationSupport            = require("enyo-anim/AnimationSupport"),
    Animator                    = require("enyo-anim/Animator"),
    Attractor                   = require("enyo-anim/Attractor"),
    Control                     = require("enyo/Control"),
    PerformanceMode             = require("enyo-perf/PerformanceMode"),
    ResizeSensorSupport         = require("enyo-ui/ResizeSensorSupport"),
    Scalar                      = require("enyo-anim/Scalar"),
    dispatcher                  = require("enyo/dispatcher"),
    isOverscrollBehaviorSupported,
    kind                        = require("enyo/kind"),
    utils                       = require("enyo/utils");

require("enyo-ui");
require("./Scroller.css");

isOverscrollBehaviorSupported = CSS
    && CSS.supports
    && CSS.supports("overscroll-behavior", "none")
    && CSS.supports("overscroll-behavior", "auto");

/**
 * [Scroller]{@link enyo-ui.Scroller} uses native scroll behavior as much as
 * possible. This makes it unsuitable for use with old IOS versions, but it does
 * keep things easy to work with.
 *
 * @ui
 * @class   enyo-ui.Scroller
 * @extends enyo.Control
 * @mixes   enyo-perf.AbsoluteShowingQueueSupport
 * @mixes   enyo-ui.AccelerateSupport
 * @public
 */
module.exports = kind(/** @lends enyo-ui.Scroller.prototype */ {

    /**
     * @private
     */
    name: "enyo-ui.Scroller",

    /**
     * @private
     */
    kind: Control,

    /**
     * @private
     */
    mixins: [
        AbsoluteShowingQueueSupport,
        AccelerateSupport,
        AnimationSupport,
        ResizeSensorSupport
    ],

    /**
     * @private
     */
    classes: "enyo-ui-scroller",

    /**
     * Whether to expand the scroller to fit it's container.
     *
     * @type     {boolean}
     * @default  false
     * @public
     */
    fit: false,

    /**
     * @type {?number|string}
     * @default null
     * @public
     */
    minWidth: null,

    /**
     * @type {?number|string}
     * @default null
     * @public
     */
    width: null,

    /**
     * @type {?number|string}
     * @default null
     * @public
     */
    maxWidth: null,

    /**
     * @type {?number|string}
     * @default null
     * @public
     */
    minHeight: null,

    /**
     * @type {?number|string}
     * @default null
     * @public
     */
    height: null,

    /**
     * @type {?number|string}
     * @default null
     * @public
     */
    maxHeight: null,

    /**
     * Whether to gpu-accelerate this controls' DOM Node.
     *
     * @type     {boolean}
     * @default  true
     * @public
     */
    accelerate: true,

    /**
     * Whether vertical scrolling is allowed.
     *
     * @type    {boolean}
     * @default true
     * @public
     */
    vertical: true,

    /**
     * Whether horizontal scrolling is allowed.
     *
     * @type    {boolean}
     * @default false
     * @public
     */
    horizontal: true,

    /**
     * Whether to use native scrollbars.
     *
     * @type    {boolean}
     * @default false
     * @public
     */
    useNativeChrome: false,

    /**
     * Whether to allow scroll-chaining.
     *
     * @type    {boolean}
     * @default true
     * @public
     */
    scrollChaining: true,

    /**
     * Multiplier that determines how quickly scroll-momentum is lost during
     * transitions. Mainly allows to prevent wiggles after transitions.
     * Lower the value to increase friction.
     *
     * @type    {number}
     * @default 0.7
     * @public
     */
    transitionFriction: 0.7,

    /**
     * Determines how quickly scroll-momentum is gained when converging
     * towards a new offset. Increase the value to increase acceleration.
     *
     * @type    {number}
     * @default 0.03
     * @public
     */
    transitionAttraction: 0.03,

    /**
     * Sets the smallest significant scroll distance. Lower values will be
     * clipped to zero. (Used to break zeno's paradox during transitions)
     *
     * @type    {number}
     * @default 0.5
     * @public
     */
    transitionEpsilon: 0.5,

    /**
     * The horizontal scroll position we're animating towards.
     *
     * @type {?number}
     * @readonly
     * @public
     */
    targetLeft: null,

    /**
     * The vertical scroll position we're animating towards.
     *
     * @type {?number}
     * @readonly
     * @public
     */
    targetTop: null,

    /**
     * Count the amount of active touches on the scroller.
     *
     * @private
     */
    numTouches: 0,

    /**
     * Whether the scroller is currently scrolling.
     *
     * @type {boolean}
     * @readonly
     * @public
     */
    scrolling: false,

    /**
     * Reference for the timeout that registers a lack of scroll-events.
     *
     * @private
     */
    scrollTimeout: null,

    /**
     * The duration of the timeout that determines we've stopped scrolling.
     *
     * @private
     */
    scrollStopDuration: 100,

    /**
     * Whether the scroller is currently animating.
     *
     * @type {boolean}
     * @readonly
     * @public
     */
    animating: false,

    /**
     * @private
     */
    lastScrollLeft: null,

    /**
     * @private
     */
    lastScrollTop: null,

    /**
     * @private
     */
    reflowing: false,

    /**
     * @type {Object}
     * @readonly
     * @public
     */
    dimensions: {

        /**
         * The width of the scroller.
         *
         * @type {number}
         * @readonly
         * @public
         */
        scrollerWidth: 0,

        /**
         * The height of the scroller.
         *
         * @type {number}
         * @readonly
         * @public
         */
        scrollerHeight: 0,

        /**
         * The width of the content.
         *
         * @type {number}
         * @readonly
         * @public
         */
        contentWidth: 0,

        /**
         * The height of the content.
         *
         * @type {number}
         * @readonly
         * @public
         */
        contentHeight: 0
    },

    /**
     * @type {Object}
     * @readonly
     * @public
     */
    scrollLimits: {

        /**
         * The maximum amount we can scroll to the right without overscrolling.
         *
         * @type {number}
         * @readonly
         * @public
         */
        maxLeft: 0,

        /**
         * The maximum amount we can scroll to the bottom without overscrolling.
         *
         * @type {number}
         * @readonly
         * @public
         */
        maxTop: 0
    },

    /**
     * @type {Object}
     * @readonly
     * @public
     */
    scrollPosition: {
        /**
         * The amount of pixels scrolled from the left, including overscroll
         *
         * @type {number}
         * @readonly
         * @public
         */
        scrollLeft: 0,

        /**
         * The amount of pixels scrolled from the top, including overscroll
         *
         * @type {number}
         * @readonly
         * @public
         */
        scrollTop: 0,

        /**
         * The amount of vertical overscroll in pixels
         *
         * @type {number}
         * @readonly
         * @public
         */
        overscrollX: 0,

        /**
         * The amount of horizontal overscroll in pixels
         *
         * @type {number}
         * @readonly
         * @public
         */
        overscrollY: 0

    },

    /**
     * @private
     */
    events: {
        onScrollStart: "",
        onScroll:      "",
        onScrollStop:  ""
    },

    /**
     * @private
     */
    handlers: {
        ondown:        "onDown",
        ondragfinish:  "onDragFinish",
        ontouchstart:  "countTouches",
        ontouchcancel: "countTouches",
        ontouchend:    "countTouches",
        onscroll:      "onNativeScroll",
        onNodeResized: "onNodeResized"
    },

    // ...........................
    // Creation

    /**
     * @method
     * @private
     */
    initComponents: kind.inherit(function(sup) {
        return function() {
            this.createAnimation();
            this.createSizeSpies();
            this.createClient();
            sup.apply(this, arguments);
        };
    }),

    /**
     * Builds the scroll animation.
     *
     * @private
     */
    createAnimation: function() {
        this.createChrome([{
            name: "animation",
            kind: Animation
        }]);
        this.$.animation.animators = {
            scrollLeft: new Animator({
                scalar:     new Scalar(),
                attractors: {
                    target: new Attractor()
                }
            }),
            scrollTop: new Animator({
                scalar:     new Scalar(),
                attractors: {
                    target: new Attractor()
                }
            })
        };
    },

    /**
     * @private
     */
    createSizeSpies: function() {
        this.sizeSpyX = new Control({
            mixins:       [ResizeSensorSupport],
            classes:      "enyo-ui-scroller__size-spy-x",
            addBefore:    this,
            bubbleTarget: this,
            bindings: [{
                from: ".bubbleTarget.absoluteShowing",
                to: ".absoluteShowing"
            }]
        });
        this.sizeSpyY = new Control({
            mixins:       [ResizeSensorSupport],
            classes:      "enyo-ui-scroller__size-spy-y",
            addBefore:    this,
            bubbleTarget: this,
            bindings: [{
                from: ".bubbleTarget.absoluteShowing",
                to: ".absoluteShowing"
            }]
        });
    },

    /**
     * Builds the element that will contain the content.
     *
     * @private
     */
    createClient: function() {
        this.controlParent = null;
        this.createComponents([{
            name:     "client",
            kind:     Control,
            mixins:   [ResizeSensorSupport],
            classes:  "enyo-ui-scroller__client",
            isChrome: true,
            showing:  true
        }, {
            name:     "scrim",
            kind:     Control,
            classes:  "enyo-ui-scroller__scrim",
            isChrome: true,
            showing:  false
        }]);
        this.controlParent = this.$.client;
    },

    /**
     * @method
     * @private
     */
    constructor: kind.inherit(function(sup) {
        return function() {
            this.dimensions       = utils.clone(this.dimensions);
            this.scrollLimits     = utils.clone(this.scrollLimits);
            this.scrollPosition   = utils.clone(this.scrollPosition);
            this.detectScrollStop = this.bindSafely(this.detectScrollStop);
            this.animationFrame   = this.bindSafely(this.animationFrame);
            sup.apply(this, arguments);
        };
    }),

    /**
     * @method
     * @private
     */
    create: kind.inherit(function(sup) {
        return function() {
            sup.apply(this, arguments);
            this.transitionFrictionChanged();
            this.transitionAttractionChanged();
            this.transitionEpsilonChanged();
            this.scrollChainingChanged();
            this.useNativeChromeChanged();
            this.horizontalChanged();
            this.verticalChanged();
            this.fitChanged();
            this.widthChanged();
            this.heightChanged();
            this.minWidthChanged();
            this.minHeightChanged();
            this.maxWidthChanged();
            this.maxHeightChanged();
        };
    }),

    // ...........................
    // Destruction

    /**
     * @method
     * @private
     */
    destroy: kind.inherit(function(sup) {
        return function() {
            this.stop();
            this.destroySizeSpies();
            sup.apply(this, arguments);
        };
    }),

    /**
     * @private
     */
    destroySizeSpies: function() {
        this.sizeSpyX.destroy();
        this.sizeSpyX = undefined;
        this.sizeSpyY.destroy();
        this.sizeSpyY = undefined;
    },

    /**
     * @method
     * @private
     */
    teardownRender: kind.inherit(function(sup) {
        return function() {
            this.animating && this.stopAnimation();
            this.set("targetLeft", null);
            this.set("targetTop", null);
            this.teardownSizeSpies();
            sup.apply(this, arguments);
        };
    }),

    /**
     * @private
     */
    teardownSizeSpies: function() {
        var sizeSpyX = this.sizeSpyX,
            sizeSpyY = this.sizeSpyY,
            node;

        if ((node = sizeSpyX.hasNode())) {
            sizeSpyX.teardownRender();
            node.remove();
        }

        if ((node = sizeSpyY.hasNode())) {
            sizeSpyY.teardownRender();
            node.remove();
        }
    },

    // ...........................
    // Layout

    /**
     * @method
     * @private
     */
    rendered: kind.inherit(function(sup) {
        return function() {
            !this.fit && this.renderSizeSpies();
            sup.apply(this, arguments);
            !this.get("scrollChaining") && this.preventScrollChaining();
            dispatcher.makeBubble(this, "scroll");
        };
    }),

    /**
     * @private
     */
    renderSizeSpies: function() {
        var node     = this.hasNode(),
            sizeSpyX = this.sizeSpyX,
            sizeSpyY = this.sizeSpyY;

        if (!node) {
            return;
        }

        if (!sizeSpyX.hasNode()) {
            sizeSpyX.parentNode = node.parentNode;
            sizeSpyX.render();
        }

        if (!sizeSpyY.hasNode()) {
            sizeSpyY.parentNode = node.parentNode;
            sizeSpyY.render();
        }
    },

    /**
     * @private
     */
    reflow: function() {
        this.reflowing = true;
        this.animating && this.stopAnimation();
        this.addStyles({
            "min-width":  null,
            "min-height": null,
            width:        null,
            height:       null
        });
        this.$.client.addStyles({
            "min-width":  null,
            "min-height": null
        });
        this.updateScrollerSize();
        this.updateCachedDimensions();
        this.updateCachedScrollLimits();
        this.updateCachedScrollPosition();
        this.reflowing = false;
    },

    // ...........................
    // Options

    /**
     * @private
     */
    useNativeChromeChanged: function() {
        if (this.get("useNativechrome")) {
            while (this.hasClass("no-native-chrome")) {
                this.removeClass("no-native-chrome");
            }
        }
        else {
            !this.hasClass("no-native-chrome") && this.addClass("no-native-chrome");
        }
    },

    /**
     * @private
     */
    horizontalChanged: function() {
        this.applyStyle("overflow-x", this.get("horizontal") ? "scroll" : "hidden");
    },

    /**
     * @private
     */
    verticalChanged: function() {
        this.applyStyle("overflow-y", this.get("vertical") ? "scroll" : "hidden");
    },

    /**
     * @private
     */
    fitChanged: function() {
        if (this.get("fit")) {
            this.teardownSizeSpies();
            this.addStyles({
                "min-width":  null,
                "min-height": null,
                width:        null,
                height:       null
            });
            !this.hasClass("enyo-fit") && this.addClass("enyo-fit");
        }
        else {
            while (this.hasClass("enyo-fit")) {
                this.removeClass("enyo-fit");
            }
            this.renderSizeSpies();
        }
        this.resize();
    },

    /**
     * @private
     */
    minWidthChanged: function() {
        var minWidth = this.get("minWidth");

        this.sizeSpyX.applyStyle("min-width", minWidth || minWidth === 0 ? minWidth : null);
    },

    /**
     * @private
     */
    widthChanged: function() {
        var width = this.get("width");

        this.sizeSpyX.applyStyle("width", width || width === 0 ? width : null);
    },

    /**
     * @private
     */
    maxWidthChanged: function() {
        var maxWidth = this.get("maxWidth");

        maxWidth     = maxWidth || maxWidth === 0 ? maxWidth : null;
        this.applyStyle("max-width", maxWidth);
        this.sizeSpyX.applyStyle("max-width", maxWidth);
    },

    /**
     * @private
     */
    minHeightChanged: function() {
        var minHeight = this.get("minHeight");

        this.sizeSpyY.applyStyle("min-height", minHeight || minHeight === 0 ? minHeight : null);
    },

    /**
     * @private
     */
    heightChanged: function() {
        var height = this.get("height");

        this.sizeSpyY.applyStyle("height", height || height === 0 ? height : null);
    },

    /**
     * @private
     */
    maxHeightChanged: function() {
        var maxHeight = this.get("maxHeight");

        maxHeight     = maxHeight || maxHeight === 0 ? maxHeight : null;
        this.applyStyle("max-height", maxHeight);
        this.sizeSpyY.applyStyle("max-height", maxHeight);
    },

    // ...........................
    // Resize Event Handling

    /**
     * When the scroller isn't absoluteShowing, we don't want to respond to node
     * resize events either. By the time the scroller shows again, it should
     * just reflow instead. However, adding and removing the reflow method from
     * the queue from inside onNodeResized feels a bit overbroad (particularly
     * the removal), so we'll use this wrapper instead.
     */
    deferredOnNodeResized: function() {
        this.reflow();
    },

    /**
     * Respond to resize events.
     *
     * @param    {Control}  sender  The sender of the event
     * @param    {Object}   event   The event object.
     * @returns  {boolean}  No further bubbling needed if it's one of our own
     *                      events.
     * @private
     */
    onNodeResized: function(sender, event) {
        var fit       = this.get("fit"),
            reflowing = this.reflowing;

        // Not showing. Instead we can just reflow once we do show again.
        if (!this.absoluteShowing && (
            event.originator === this.sizeSpyX
            || event.originator === this.sizeSpyY
            || event.originator === this
            || event.originator === this.$.client
        )) {
            this.addToAbsoluteShowingQueue("deferredOnNodeResized");

            return true;
        }
        this.removeFromAbsoluteShowingQueue("deferredOnNodeResized");

        if (event.originator === this.sizeSpyX
            || event.originator === this.sizeSpyY) {
            if (!reflowing && !fit) {
                this.updateScrollerSize();
                this.updateCachedDimensions();
                this.updateCachedScrollLimits();
                this.updateCachedScrollPosition();
            }

            return true;
        }
        else if (event.originator === this) {
            !reflowing && this.onScrollerResized(event);

            return true;
        }
        else if (event.originator === this.$.client) {
            !reflowing && !fit && this.updateScrollerSize();
            this.onClientResized(event);

            return true;
        }

        return false;
    },

    /**
     * @param  {Object=}  event  The resize event
     * @private
     */
    onScrollerResized: function(event) {
        var bounds         = event || this.getBounds(),
            dimensions     = this.dimensions,
            scrollerWidth  = Math.round(bounds.width || 0),
            scrollerHeight = Math.round(bounds.height || 0);

        // Update bounds only as needed
        if (scrollerWidth !== dimensions.scrollerWidth
            || scrollerHeight !== dimensions.scrollerHeight) {
            this.set("dimensions", {
                scrollerWidth:  scrollerWidth,
                scrollerHeight: scrollerHeight,
                contentWidth:   dimensions.contentWidth,
                contentHeight:  dimensions.contentHeight
            });
            this.updateCachedScrollLimits();
            this.updateCachedScrollPosition();
        }
    },

    /**
     * @param  {Object=}  event  The resize event
     * @private
     */
    onClientResized: function(event) {
        var clientBounds  = event || this.$.client.getBounds(),
            dimensions    = this.dimensions,
            contentWidth  = dimensions.contentWidth,
            contentHeight = dimensions.contentHeight;

        // Determine content bounds
        contentWidth  = Math.round(clientBounds.width || 0);
        contentHeight = Math.round(clientBounds.height || 0);

        // Update dimensions only as needed
        if (contentWidth !== dimensions.contentWidth
            || contentHeight !== dimensions.contentHeight) {
            this.set("dimensions", {
                scrollerWidth:  dimensions.scrollerWidth,
                scrollerHeight: dimensions.scrollerHeight,
                contentWidth:   contentWidth,
                contentHeight:  contentHeight
            });
            this.updateCachedScrollLimits();
            this.updateCachedScrollPosition();
        }
    },

    // ...........................
    // Performance Mode

    /**
     * Enable PerformanceMode, but only if we didn't enable it already.
     *
     * @private
     */
    enablePerformanceMode: function() {
        if (!this.performanceMode) {
            PerformanceMode.enable();
            this.performanceMode = true;
        }
    },

    /**
     * Disable PerformanceMode, but only if we didn't disable it already.
     *
     * @private
     */
    disablePerformanceMode: function() {
        if (this.performanceMode) {
            PerformanceMode.disable();
            this.performanceMode = false;
        }
    },

    // ...........................
    // Prevent Scroll Chaining

    /**
     * @param  {boolean}  wasAllowed  Whether scrollChaining was allowed before.
     * @private
     */
    scrollChainingChanged: function(wasAllowed) {
        var isAllowed = this.get("scrollChaining");

        if (wasAllowed && !isAllowed) {
            this.preventScrollChaining();
        }
        else if (!wasAllowed && isAllowed) {
            this.allowScrollChaining();
        }
    },

    /**
     * Sets the overscroll-behavior CSS property if supported.
     *
     * @private
     */
    preventScrollChaining: function() {
        if (isOverscrollBehaviorSupported) {
            this.applyStyle("overscroll-behavior", "none");
        }
    },

    /**
     * Sets the overscroll-behavior CSS property if supported.
     *
     * @private
     */
    allowScrollChaining: function() {
        if (isOverscrollBehaviorSupported) {
            this.applyStyle("overscroll-behavior", "auto");
        }
    },

    // ...........................
    // Scroll Events

    /**
     * Touching the scroller should stop any animations.
     *
     * @private
     */
    onDown: function() {
        this.animating && this.stopAnimation();
    },

    /**
     * Prevent scroll gestures from turning into taps.
     *
     * @param  {Control}  sender  The sender of the event
     * @param  {Object}   event   The event object
     * @private
     */
    onDragFinish: function(sender, event) {
        if (event && event.preventTap) {
            event.preventTap();
        }
    },

    /**
     * Keeps track of the amount of touches.
     *
     * @param  {Control}  sender  The sender of the event
     * @param  {Object}   event   The event object
     * @private
     */
    countTouches: function(sender, event) {
        var numTouches;
        this.numTouches = numTouches = event.touches.length;
        this.applyStyle("will-change", numTouches === 1 ? "scroll-position" : "auto");
    },

    /**
     * Handler for the browser's scroll events.
     *
     * @returns  {boolean}  No further bubbling needed.
     * @private
     */
    onNativeScroll: function() {
        !this.scrolling && this.onScrollStart();
        this.onScroll();

        return true;
    },

    /**
     * @private
     */
    onScrollStart: function() {
        this.$.scrim.show();
        this.set("scrolling", true);
        this.doScrollStart();
    },

    /**
     * @private
     */
    onScroll: function() {
        this.enablePerformanceMode();
        this.updateCachedScrollPosition();

        // Native scroll doesn't have a scroll stop event, instead, we'll wait a
        // little while and if no new scroll events come in we consider that the
        // end of the scroll
        if (!this.animating) {
            this.scrollTimeout && clearTimeout(this.scrollTimeout);
            this.scrollTimeout = setTimeout(this.detectScrollStop, this.scrollStopDuration);
        }

        this.doScroll();
    },

    /**
     * @private
     */
    detectScrollStop: function() {
        if (this.scrolling) {
            if (this.numTouches) {
                this.onScrollPause();
                this.scrollTimeout = setTimeout(this.detectScrollStop, this.scrollStopDuration);
            }
            else {
                this.onScrollStop();
            }
        }
    },

    /**
     * @private
     */
    onScrollPause: function() {
        this.disablePerformanceMode();
    },

    /**
     * @private
     */
    onScrollStop: function() {
        this.scrollTimeout && clearTimeout(this.scrollTimeout);
        this.scrollTimeout = null;
        this.$.scrim.hide();
        this.disablePerformanceMode();
        this.set("scrolling", false);
        this.doScrollStop();
    },

    // ...........................
    // Scroller Metrics

    /**
     * @private
     */
    updateScrollerSize: function() {
        var scrollerBounds,
            sizeSpyXBounds,
            sizeSpyYBounds,
            clientBounds;

        if (this.get("fit")) {
            scrollerBounds = this.getBounds();
            this.$.client.addStyles({
                "min-width":  scrollerBounds.width + "px",
                "min-height": scrollerBounds.height + "px"
            });
        }
        else {
            sizeSpyXBounds = this.sizeSpyX.getBounds();
            sizeSpyYBounds = this.sizeSpyY.getBounds();

            this.$.client.addStyles({
                "min-width":  sizeSpyXBounds.width + "px",
                "min-height": sizeSpyYBounds.height + "px"
            });
            clientBounds = this.$.client.getBounds();

            this.addStyles({
                "min-width":  this.get("minWidth") ? sizeSpyXBounds.width + "px" : "",
                "min-height": this.get("minHeight") ? sizeSpyYBounds.height + "px" : "",
                width:        this.get("width")
                    ? Math.min(sizeSpyXBounds.width, clientBounds.width) + "px"
                    : clientBounds.width + "px",
                height: this.get("height")
                    ? Math.min(sizeSpyYBounds.height, clientBounds.height) + "px"
                    : clientBounds.height + "px"
            });
        }
    },

    /**
     * Resolves the dimensions of the scroller and updates the cached value as
     * needed.
     *
     * @private
     */
    updateCachedDimensions: function() {
        var currentDimensions  = this.dimensions,
            newDimensions,
            resizeSensor       = this.resizeSensor,
            clientResizeSensor = this.$.client.resizeSensor;

        newDimensions = this.resolveDimensions();

        if (currentDimensions.scrollerWidth !== newDimensions.scrollerWidth
            || currentDimensions.scrollerHeight !== newDimensions.scrollerHeight
            || currentDimensions.contentWidth !== newDimensions.contentWidth
            || currentDimensions.contentHeight !== newDimensions.contentHeight) {
            // We rely on the resize sensors to report unexpected changes. To
            // keep the sensors from reporting known changes, we inform them of
            // our new known dimensions.
            resizeSensor.width        = newDimensions.scrollerWidth;
            resizeSensor.height       = newDimensions.scrollerHeight;
            clientResizeSensor.width  = newDimensions.contentWidth;
            clientResizeSensor.height = newDimensions.contentHeight;
            this.set("dimensions", newDimensions);
        }
    },

    /**
     * Resolves the maximum scroll offset and updates the cached value as
     * needed.
     *
     * @private
     */
    updateCachedScrollLimits: function() {
        var currentLimits = this.scrollLimits,
            newLimits;

        newLimits = this.resolveScrollLimits();

        if (currentLimits.maxLeft !== newLimits.maxLeft
            || currentLimits.maxTop !== newLimits.maxTop) {
            this.set("scrollLimits", newLimits);
        }
    },

    /**
     * Verifies is position of the scroller and updates the cached value as
     * needed.
     *
     * @private
     */
    updateCachedScrollPosition: function() {
        var animation       = this.$.animation,
            animators       = animation.animators,
            scrollLeft      = animators.scrollLeft.scalar,
            scrollTop       = animators.scrollTop.scalar,
            currentPosition = this.scrollPosition,
            newPosition;

        newPosition = this.resolveScrollPosition();

        if (currentPosition.scrollLeft !== newPosition.scrollLeft
            || currentPosition.scrollTop !== newPosition.scrollTop
            || currentPosition.overscrollX !== newPosition.overscrollX
            || currentPosition.overscrollY !== newPosition.overscrollY) {
            scrollLeft.value = newPosition.scrollLeft;
            scrollTop.value  = newPosition.scrollTop;
            this.set("scrollPosition", newPosition);
        }
    },

    /**
     * Resolves scroller and content dimensions and returns an object describing
     * the dimensions.
     *
     * @returns  {Object}  The dimensions
     * @private
     */
    resolveDimensions: function() {
        var bounds       = this.getBounds(),
            clientBounds = this.$.client.getBounds();

        return {
            scrollerWidth:  Math.round(bounds.width || 0),
            scrollerHeight: Math.round(bounds.height || 0),
            contentWidth:   Math.round(clientBounds.width || 0),
            contentHeight:  Math.round(clientBounds.height || 0)
        };
    },

    /**
     * Calculates the maximum scroll offset and returns an object describing the
     * limits.
     *
     * @returns  {Object}  The limits
     * @private
     */
    resolveScrollLimits: function() {
        var dimensions   = this.dimensions,
            scrollLimits = {};

        scrollLimits.maxLeft = Math.max(0, dimensions.contentWidth - dimensions.scrollerWidth);
        scrollLimits.maxTop  = Math.max(0, dimensions.contentHeight - dimensions.scrollerHeight);

        return scrollLimits;
    },

    /**
     * Retrieved scroll position from DOM, calculates overscroll and returns the
     * an object describing the scroll position.
     *
     * @returns  {Object}  The scrollPosition
     * @private
     */
    resolveScrollPosition: function() {
        var node           = this.hasNode() || {},
            scrollLimits   = this.scrollLimits,
            scrollLeft     = node.scrollLeft || 0,
            scrollTop      = node.scrollTop || 0,
            overscrollX    = 0,
            overscrollY    = 0,
            scrollPosition = {};

        // Calculate horizontal overscroll
        if (scrollLeft < 0) {
            overscrollX = scrollLeft;
        }
        else if (scrollLeft > scrollLimits.maxLeft) {
            overscrollX = scrollLeft - scrollLimits.maxLeft;
        }

        // Calculate vertical overscroll
        if (scrollTop < 0) {
            overscrollY = scrollTop;
        }
        else if (scrollTop > scrollLimits.maxTop) {
            overscrollY = scrollTop - scrollLimits.maxTop;
        }

        // Update scrollPosition
        scrollPosition.scrollLeft  = node.scrollLeft;
        scrollPosition.scrollTop   = node.scrollTop;
        scrollPosition.overscrollX = overscrollX;
        scrollPosition.overscrollY = overscrollY;

        return scrollPosition;
    },

    /**
     * Clips the scrollLeft value to remove any overscroll.
     *
     * @param    {number}  scrollLeft  The scrollLeft value
     * @returns  {number}  The clipped scrollLeft value
     * @private
     */
    clipScrollLeft: function(scrollLeft) {
        return Math.max(0, Math.min(scrollLeft, this.scrollLimits.maxLeft));
    },

    /**
     * Clips the scrollTop value to remove any overscroll.
     *
     * @param    {number}  scrollTop  The scrollTop value
     * @returns  {number}  The clipped scrollTop value
     * @private
     */
    clipScrollTop: function(scrollTop) {
        return Math.max(0, Math.min(scrollTop, this.scrollLimits.maxTop));
    },

    // ...........................
    // Animation

    /**
     * @private
     */
    transitionFrictionChanged: function() {
        var friction  = this.transitionFriction,
            animators = this.$.animation.animators;

        animators.scrollLeft.scalar.friction = friction;
        animators.scrollTop.scalar.friction  = friction;
    },

    /**
     * @private
     */
    transitionAttractionChanged: function() {
        var attraction = this.transitionAttraction,
            animators  = this.$.animation.animators;

        animators.scrollLeft.attractors.target.attraction = attraction;
        animators.scrollTop.attractors.target.attraction  = attraction;
    },

    /**
     * @private
     */
    transitionEpsilonChanged: function() {
        var epsilon   = this.transitionEpsilon,
            animators = this.$.animation.animators;

        animators.scrollLeft.scalar.epsilon = epsilon;
        animators.scrollTop.scalar.epsilon  = epsilon;
    },

    /**
     * @private
     */
    targetLeftChanged: function() {
        this.$.animation.animators.scrollLeft.attractors.target.value = this.targetLeft;
    },

    /**
     * @private
     */
    targetTopChanged: function() {
        this.$.animation.animators.scrollTop.attractors.target.value = this.targetTop;
    },

    /**
     * Start animating.
     *
     * @public
     */
    startAnimation: function() {
        var animation  = this.$.animation,
            animators  = animation.animators,
            scrollLeft = animators.scrollLeft.scalar,
            scrollTop  = animators.scrollTop.scalar;

        if (this.animating) {
            return;
        }

        // Since we'll be taking over the scrolling, we don't need to watch
        // native scroll events to detect the end of a native momentum-scroll.
        this.scrollTimeout && clearTimeout(this.scrollTimeout);
        this.scrollTimeout = null;

        // Ensure accurate scroll position
        this.updateCachedScrollPosition();

        // Memorize current scrollPosition
        this.lastScrollLeft = scrollLeft.value;
        this.lastScrollTop  = scrollTop.value;

        this.set("animating", true);
        this.animationFrame();
    },

    /**
     * Execute a single animation frame.
     *
     * @private
     */
    animationFrame: function() {
        var animators      = this.$.animation.animators,
            scrollLeft     = animators.scrollLeft.scalar,
            scrollTop      = animators.scrollTop.scalar,
            targetLeft     = animators.scrollLeft.attractors.target,
            targetTop      = animators.scrollTop.attractors.target,
            lastScrollLeft = this.lastScrollLeft,
            lastScrollTop  = this.lastScrollTop,
            horizontal     = this.get("horizontal"),
            vertical       = this.get("vertical");

        if (!this.animating) {
            return;
        }

        // Stop scrolling if not showing
        if (!this.absoluteShowing) {
            this.finishAnimation();

            return;
        }

        // Update horizontal scroll position
        if (horizontal && !animators.scrollLeft.isDone()) {
            animators.scrollLeft.update();

            if (animators.scrollLeft.isDone()) {
                animators.scrollLeft.finish();
            }

            // Finish as soon as the target is reached or overshot.
            if (scrollLeft.value >= lastScrollLeft && scrollLeft.value >= targetLeft.value) {
                animators.scrollLeft.snap();
            }
            else if (scrollLeft.value <= lastScrollLeft && scrollLeft.value <= targetLeft.value) {
                animators.scrollLeft.snap();
            }

            this.lastScrollLeft = scrollLeft.value;
        }

        // Update vertical scroll position
        if (vertical && !animators.scrollTop.isDone()) {
            animators.scrollTop.update();

            if (animators.scrollTop.isDone()) {
                animators.scrollTop.finish();
            }

            // Finish as soon as the target is reached or overshot.
            if (scrollTop.value >= lastScrollTop && scrollTop.value >= targetTop.value) {
                animators.scrollTop.snap();
            }
            else if (scrollTop.value <= lastScrollTop && scrollTop.value <= targetTop.value) {
                animators.scrollTop.snap();
            }

            this.lastScrollTop = scrollTop.value;
        }

        // Apply scroll position
        this.applyAnimatedScrollPosition();

        // While the browser will trigger a scroll event, it will do so
        // asynchronously. It's much more intuitive if the scroller enters it's
        // scrolling state immediately, so we'll do that now.
        if (!this.scrolling) {
            this.onScrollStart();
            this.onScroll();
        }

        // Schedule the next frame as needed
        if ((horizontal && !animators.scrollLeft.isDone())
            || (vertical && !animators.scrollTop.isDone())) {
            this.requestAnimationFrame("animationFrame", this.animationFrame);
        }
        else {
            this.stopAnimation();
        }
    },

    /**
     * Takes the scroll position from the animation and applies it to the DOM.
     *
     * @private
     */
    applyAnimatedScrollPosition: function() {
        var node       = this.hasNode(),
            animators  = this.$.animation.animators,
            scrollLeft = animators.scrollLeft.scalar,
            scrollTop  = animators.scrollTop.scalar;

        if (!this.animating || !node) {
            return;
        }

        if (this.get("horizontal")) {
            node.scrollLeft = Math.round(scrollLeft.value);
        }

        if (this.get("vertical")) {
            node.scrollTop = Math.round(scrollTop.value);
        }
    },

    /**
     * Instantly skips to the end of the animation.
     *
     * @public
     */
    finishAnimation: function() {
        var animators = this.$.animation.animators;

        if (!this.animating) {
            return;
        }

        this.get("horizontal") && animators.scrollLeft.finish();
        this.get("vertical") && animators.scrollTop.finish();
        this.applyAnimatedScrollPosition();
        this.stopAnimation();
    },

    /**
     * Stops the animation.
     *
     * @public
     */
    stopAnimation: function() {
        var animation = this.$.animation;

        if (!this.animating) {
            return;
        }

        // Clear animation targets
        animation.stop();
        animation.clearAttractors();
        this.cancelAnimationFrame("animationFrame", this.animationFrame);
        this.set("animating", false);

        // We're done scrolling now
        this.scrolling && this.onScrollStop();
    },

    // ...........................
    // Issers

    /**
     * Whether our scroller is currently overscrolling.
     *
     * @return {boolean}  `true` if overscrolling, `false` otherwise
     * @public
     */
    isOverscrolling: function() {
        var scrollPosition = this.scrollPosition;

        return scrollPosition.overscrollX !== 0 || scrollPosition.overscrollY !== 0;
    },

    /**
     * Whether our scroller is scrolled all the way to the top or beyond.
     *
     * @return {boolean}  `true` if scrolled to top, `false` otherwise
     * @public
     */
    isAtTop: function() {
        return this.scrollPosition.scrollTop <= 0;
    },

    /**
     * Whether our scroller is scrolled all the way to the bottom or beyond.
     *
     * @return {boolean}  `true` if scrolled to bottom, `false` otherwise
     * @public
     */
    isAtBottom: function() {
        return this.scrollPosition.scrollTop >= this.scrollLimits.maxTop;
    },

    /**
     * Whether our scroller is scrolled all the way to the left.
     *
     * @return {boolean}  `true` if scrolled to left, `false` otherwise
     * @public
     */
    isAtLeft: function() {
        return this.scrollPosition.scrollLeft <= 0;
    },

    /**
     * Whether our scroller is scrolled all the way to the right.
     *
     * @return {boolean}  `true` if scrolled to right, `false` otherwise
     * @public
     */
    isAtRight: function() {
        return this.scrollPosition.scrollLeft >= this.scrollLimits.maxLeft;
    },

    // ...........................
    // Scroll API

    /**
     * Transition to a new scroll position by supplying absolute coordinates.
     * Automatically starts an animation if needed.
     *
     *  - Use an integer value to set a new scroll target.
     *  - Use `null` to unset a scroll target.
     *  - Use `undefined` to leave the target unchanged.
     *
     * @param  {int|null|undefined}  x  The x-coordinate.
     * @param  {int|null|undefined}  y  The y-coordinate.
     * @public
     */
    scrollTo: function(x, y) {
        if (typeof x === "number") {
            x = this.clipScrollLeft(x);
            this.set("targetLeft", x, true);
        }

        if (typeof y === "number") {
            y = this.clipScrollTop(y);
            this.set("targetTop", y, true);
        }
        !this.animating && this.startAnimation();
    },

    /**
     * Scroll to the top, with an animated transition.
     *
     * @private
     */
    scrollToTop: function() {
        this.scrollTo(undefined, 0);
    },

    /**
     * Scroll to the bottom, with an animated transition.
     *
     * @private
     */
    scrollToBottom: function() {
        var scrollLimits = this.scrollLimits;

        this.scrollTo(undefined, scrollLimits.maxTop);
    },

    /**
     * Scroll to the left, with an animated transition.
     *
     * @private
     */
    scrollToLeft: function() {
        this.scrollTo(0, undefined);
    },

    /**
     * Scroll to the right, with an animated transition.
     *
     * @private
     */
    scrollToRight: function() {
        var scrollLimits = this.scrollLimits;

        this.scrollTo(scrollLimits.maxLeft, undefined);
    },

    /**
     * Transition to a new scroll position by supplying coordinates relative to
     * the current scroll position. Automatically starts an animation if needed.
     *
     *  - Use an integer value to set a new scroll target.
     *  - Use `null` to unset a scroll target.
     *  - Use `undefined` to leave the target unchanged.
     *
     * @param  {int|null|undefined}  x  The x-coordinate.
     * @param  {int|null|undefined}  y  The y-coordinate.
     * @public
     */
    scrollBy: function(x, y) {
        var scrollPosition = this.scrollPosition;

        if (typeof x === "number") {
            x = scrollPosition.scrollLeft + x;
        }

        if (typeof y === "number") {
            y = scrollPosition.scrollTop + y;
        }

        this.scrollTo(x, y);
    },

    /**
     * Transition to a new scroll position by supplying coordinates relative to
     * the current scroll target. If no scroll target is present, the
     * coordinates will be applied relative to the current scroll position.
     * Automatically starts an animation if needed.
     *
     *  - Use an integer value to set a new scroll target.
     *  - Use `null` to unset a scroll target.
     *  - Use `undefined` to leave the target unchanged.
     *
     * @param  {int|null|undefined}  x  The x-coordinate.
     * @param  {int|null|undefined}  y  The y-coordinate.
     * @public
     */
    extendScrollBy: function(x, y) {
        var scrollPosition = this.scrollPosition,
            targetLeft     = this.targetLeft,
            targetTop      = this.targetTop;

        if (typeof x === "number") {
            x += targetLeft === null ? scrollPosition.scrollLeft : targetLeft;
        }

        if (typeof y === "number") {
            y += targetTop === null ? scrollPosition.scrollTop : targetTop;
        }

        this.scrollTo(x, y);
    },

    /**
     * Snaps to a new scroll position by supplying absolute coordinates.
     *
     *  - Use an integer value to snap to a new scroll position.
     *  - Use `undefined` to leave the target and position unchanged.
     *
     * @param  {int|undefined}  x  The x-coordinate.
     * @param  {int|undefined}  y  The y-coordinate.
     * @public
     */
    snapTo: function(x, y) {
        var animators  = this.$.animation.animators,
            scrollLeft = animators.scrollLeft,
            scrollTop  = animators.scrollTop,
            node       = this.hasNode();

        if (x !== undefined) {
            this.set("targetLeft", null);
            !scrollLeft.isDone() && scrollLeft.stop();

            if (node) {
                node.scrollLeft = Math.round(x);
            }
        }

        if (y !== undefined) {
            this.set("targetTop", null);
            !scrollTop.isDone() && scrollTop.stop();

            if (node) {
                node.scrollTop = Math.round(y);
            }
        }

        this.updateCachedScrollPosition();
    },

    /**
     * Snap to the top.
     *
     * @private
     */
    snapToTop: function() {
        this.snapTo(undefined, 0);
    },

    /**
     * Snap to the bottom.
     *
     * @private
     */
    snapToBottom: function() {
        var scrollLimits = this.scrollLimits;

        this.snapTo(undefined, scrollLimits.maxTop);
    },

    /**
     * Snap to the left.
     *
     * @private
     */
    snapToLeft: function() {
        this.snapTo(0, undefined);
    },

    /**
     * Snap to the right.
     *
     * @private
     */
    snapToRight: function() {
        var scrollLimits = this.scrollLimits;

        this.snapTo(scrollLimits.maxLeft, undefined);
    },

    /**
     * Snaps to a new scroll position by supplying coordinates relative to
     * the current scroll position.
     *
     *  - Use an integer value to snap to a new scroll position.
     *  - Use `undefined` to leave the target and position unchanged.
     *
     * @param  {int|undefined}  x  The x-coordinate.
     * @param  {int|undefined}  y  The y-coordinate.
     * @public
     */
    snapBy: function(x, y) {
        var scrollPosition = this.scrollPosition;

        if (typeof x === "number") {
            x = scrollPosition.scrollLeft + x;
        }

        if (typeof y === "number") {
            y = scrollPosition.scrollTop + y;
        }

        this.snapTo(x, y);
    },

    /**
     * Snaps to a new scroll position by supplying coordinates relative to the
     * current scroll target. If no scroll target is present, the coordinates
     * will be applied relative to the current scroll position.
     *
     *  - Use an integer value to set a new scroll target.
     *  - Use `undefined` to leave the target unchanged.
     *
     * @param  {int|null|undefined}  x  The x-coordinate.
     * @param  {int|null|undefined}  y  The y-coordinate.
     * @public
     */
    extendSnapBy: function(x, y) {
        var scrollPosition = this.scrollPosition,
            targetLeft     = this.targetLeft,
            targetTop      = this.targetTop;

        if (typeof x === "number") {
            x += targetLeft === null ? scrollPosition.scrollLeft : targetLeft;
        }

        if (typeof y === "number") {
            y += targetTop === null ? scrollPosition.scrollTop : targetTop;
        }

        this.snapTo(x, y);
    },

    /**
     * Stops the scroller at it's current position.
     *
     * @public
     */
    stop: function() {
        var scrollPosition = this.scrollPosition,
            x              = scrollPosition.scrollLeft,
            y              = scrollPosition.scrollTop;

        // Stop animation as needed
        if (this.animating) {
            this.stopAnimation();
        }

        // Stop native scroll as needed
        else if (this.scrolling) {
            // This should stop any momentum scrolling
            this.snapTo(x, y);

            // Normally we use a timeout to wait for the end of momentum-
            // scrolling. In this case, we know we've stopped so we can fire the
            // handler immediately.
            this.onScrollStop();
        }
    }

});
